// SUB-OFFICE 7 — THE ZONE MANAGER (increment M-B). The N-zone streaming spine
// that generalizes the two-zone M-A framework (arrival.ts's hide-interior /
// swap-collision / spawn dance) into a registry every future zone plugs into.
//
// Model (spec-map-v2 tech_architecture.zone_streaming_strategy): the RESIDENT
// interior floor (floor7 + its attached lobby fiction) is never disposed while
// you are on it — it is toggled via visibility. At most ONE STREAMED scene is
// built at a time (machine_floor / suboffice4 / records / the M-B descent
// stub); entering one disposes the previously-streamed scene, builds the target
// from its manifest (~<400ms), swaps the active collision-grid pointer, and
// spawns the capsule at a named spawn. The elevator cab (cab.ts) is the mask
// under which a streamed transition's build/dispose is hidden; unmasked walks
// (interior <-> lobby) call travel() directly.
//
// This is deliberately minimal but real: it owns build/dispose + grid-swap +
// spawn + FP-enforce, and nothing else. M-D's machine_floor registers here as
// one more streamed record; the cab route already covers its full-swap rebuild.

import type { Scene } from 'three';
import { useZoneCollision, restoreInteriorCollision } from '../collision';
import type { ViewMode } from '../../render/scene';
import type { FirstPersonView } from '../../render/fpview';
import type { Player } from '../../entities/player';
import type { ZoneManifest, ZoneSpawn } from './types';

/** The minimal surface a built streamed zone exposes to the manager. Concrete
 *  builds (VerticalCoreBuild, StubBuild, …) satisfy this structurally and may
 *  carry extra per-zone fields the owning controller consumes. */
export interface ZoneBuild {
  group: import('three').Group;
  manifest: ZoneManifest;
  tris: number;
  buildMs: number;
  dispose(): void;
}

export type ZoneResidency = 'resident' | 'streamed';

export interface ZoneRecord {
  id: string;
  residency: ZoneResidency;
  /** streamed zones: the build fn (called on travel); resident zones: omit. */
  build?: (reducedMotion: boolean) => ZoneBuild;
  /** resident zones: named spawns (streamed zones carry theirs in the build). */
  spawns?: Record<string, ZoneSpawn>;
}

export interface ZoneManagerDeps {
  scene: Scene;
  player: Player;
  fpv: FirstPersonView;
  applyView: (v: ViewMode) => void;
  setInteriorVisible: (on: boolean) => void;
  reducedMotion: boolean;
}

export class ZoneManager {
  private records = new Map<string, ZoneRecord>();
  private builtStreamed: ZoneBuild | null = null;
  private activeId = 'floor7';

  constructor(private deps: ZoneManagerDeps) {}

  register(rec: ZoneRecord): void {
    this.records.set(rec.id, rec);
  }

  get active(): string { return this.activeId; }
  /** The currently built streamed scene, or null when standing on the resident floor. */
  get current(): ZoneBuild | null { return this.builtStreamed; }

  /**
   * Travel to a registered zone and spawn the capsule at `spawnName`. Disposes
   * any previously-streamed scene, builds the target if streamed, swaps the
   * active collision grid, and re-asserts FP. Returns the built streamed scene
   * (or null when the target is the resident floor). Callers that want the
   * transition masked wrap this in CabMask.ride(onSwap: () => manager.travel()).
   */
  travel(toId: string, spawnName: string): ZoneBuild | null {
    const rec = this.records.get(toId);
    if (!rec) throw new Error(`unknown zone ${toId}`);
    const d = this.deps;

    if (rec.residency === 'resident') {
      this.disposeStreamed();
      restoreInteriorCollision();
      d.setInteriorVisible(true);
      const sp = rec.spawns?.[spawnName];
      if (sp) this.place(sp);
      this.activeId = toId;
      d.applyView('FP');
      return null;
    }

    // streamed: hide the resident floor, dispose the previous streamed scene,
    // build the target, install its grid, spawn.
    if (!rec.build) throw new Error(`streamed zone ${toId} has no build fn`);
    d.setInteriorVisible(false);
    this.disposeStreamed();
    const build = rec.build(d.reducedMotion);
    d.scene.add(build.group);
    const col = build.manifest.collision;
    if (col) {
      useZoneCollision({ walls: col.walls, solids: col.solids, zones: col.zones, bounds: build.manifest.bounds });
    }
    const sp = build.manifest.spawns[spawnName];
    if (sp) this.place(sp);
    this.builtStreamed = build;
    this.activeId = toId;
    d.applyView('FP');
    return build;
  }

  private place(sp: ZoneSpawn): void {
    this.deps.player.setPosition(sp.x, sp.z, sp.facing);
    this.deps.fpv.syncFromFacing(sp.facing);
  }

  private disposeStreamed(): void {
    if (!this.builtStreamed) return;
    this.deps.scene.remove(this.builtStreamed.group);
    this.builtStreamed.dispose();
    this.builtStreamed = null;
  }

  /** Full teardown (used if the whole vertical-core session is abandoned). */
  disposeAll(): void {
    this.disposeStreamed();
    restoreInteriorCollision();
    this.activeId = 'floor7';
  }
}
